import React, {FC, useCallback, useEffect, useRef, useState} from "react";
import {v4 as uuidv4} from "uuid";
import classnames from "classnames";
import {Dayjs} from "dayjs";

import {
	Button,
	CalendarButton,
	EmojiPickerInput,
	FileUpload,
	InputRow,
	Separator,
	Text,
} from "../../components/input";
import {OpenGraph, REFRESH_SHUFFLE_AI, Service, useMyUser} from "../../data";
import {PostServiceType} from "../../data/collection";
import {useMutationToast, useToast} from "../../toast";
import {useDirtyCopy} from "../../dirty-copy";
import {Span3} from "../../components/text";
import {Arrow, Icon} from "../../components/images";
import {OpengraphMedia} from "../../components/opengraph";
import {EmptyComponent} from "../../types";
import {post as postApi} from "../../api";
import {useMediaLibraryModal} from "../../modals/media/media-library";
import {inflateUploadedImage, Media} from "../../data/media";
import {Loading} from "../../components/loading";
import {MentionsText} from "../../components/input/mentions-text";

import styles from "./post.module.scss";

interface BaseOGEditorProps extends EmptyComponent {
	opengraph: OpenGraph;
	url: string | null | undefined;
	updateOpengraph: (changes: Partial<OpenGraph>) => void;
	isURL: boolean;
	startOpen?: boolean;
	placeholder?: string;
	peakTime?: boolean;
	isLoading?: boolean;
	onRemove?: () => void;
	displayRemoveIcon?: boolean;
}

interface BulkEditorProps extends BaseOGEditorProps {
	schedule: () => void;
	network?: never;
	setScheduledFor?: never;
	scheduledFor?: never;
	scheduling?: never;
	disabled?: boolean;
	hasErrors?: boolean;
}

interface ScheduleEditorProps extends BaseOGEditorProps {
	schedule?: never;
	network: Service;
	setScheduledFor: (value: Dayjs) => void;
	scheduledFor?: Dayjs;
	scheduling?: boolean;
	disabled?: boolean;
	hasErrors?: boolean;
}

export const OGEditor: FC<BulkEditorProps | ScheduleEditorProps> = ({
	isURL,
	isLoading,
	url,
	displayRemoveIcon,
	onRemove,
	network,
	opengraph,
	schedule,
	scheduling,
	scheduledFor,
	setScheduledFor,
	startOpen,
	updateOpengraph,
	placeholder,
	disabled,
	hasErrors,
	peakTime,
}) => {
	const [open, setOpen] = useState(startOpen);
	const [file, setFile] = useState<File>();
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	const me = useMyUser();
	const toast = useToast();

	const [refreshSocialShuffle] = useMutationToast(REFRESH_SHUFFLE_AI);
	const handleMediaUpdate = useCallback(
		(selectedMedia: Media) =>
			updateOpengraph({
				image: selectedMedia.type === "image" ? selectedMedia.url : undefined,
				video: selectedMedia.type === "video" ? selectedMedia.url : undefined,
			}),
		[updateOpengraph]
	);

	const mediaLibraryModal = useMediaLibraryModal({onConfirm: handleMediaUpdate});
	const handleAIUpdate = useCallback(
		({discard, val: aiChat}) =>
			refreshSocialShuffle({
				variables: {
					aiChat: {
						id: aiChat.id,
						message: aiChat.message,
						service: aiChat.service,
						prompt: opengraph.comment,
					},
				},
				onCompleted: ({refreshSocialShuffle: {message}}) => {
					discard();
					updateOpengraph({comment: message});
				},
			}),
		[refreshSocialShuffle, updateOpengraph, opengraph.comment]
	);
	const {update: updateAIChat, dirty: dirtyAIChat} = useDirtyCopy(
		{
			message: "",
			prompt: "",
			id: uuidv4(),
			service: "GENERAL",
		},
		{debounce: 500, onUpdate: handleAIUpdate}
	);

	useEffect(() => {
		if (!file) return;

		const type = "image";
		const formData = new FormData();
		formData.append(type, file);
		formData.append("name", type);

		postApi(`/${type}s/${type}-list`, formData, {}, false)
			.then(data => updateOpengraph({image: inflateUploadedImage(data ?? {}).url}))
			.catch(error => toast({color: "red", text: error, icon: "warning"}))
			.finally(() => setFile(undefined));
	}, [file, toast, updateOpengraph]);

	const handleEmojiChange = (emoji: string) => {
		const pos = textareaRef?.current?.selectionStart ?? 0;
		const comment = opengraph.comment ?? "";

		updateOpengraph({comment: `${comment.slice(0, pos)}${emoji}${comment.slice(pos)}`});
		const focusInput = () => {
			textareaRef?.current?.focus();
			textareaRef?.current?.setSelectionRange(pos + emoji.length, pos + emoji.length);
		};

		setTimeout(focusInput, 0);
	};

	return (
		<div className={classnames(styles.postItem, "space", hasErrors && styles.postError)}>
			<div className={classnames(styles.postHeader, "space")}>
				{network ? (
					<Icon className={classnames(open ? styles.tileElementOpen : null)} icon={network} />
				) : (
					<div />
				)}
				{open ? (
					<MentionsText
						ref={textareaRef}
						type="textarea"
						placeholder="Write a comment or paste a link to share"
						onChange={comment => updateOpengraph({comment})}
						value={opengraph.comment}
						bare
						service={PostServiceType[network || "general"]}
						info={network === "twitter" ? `${opengraph.comment?.length || 0}/255` : undefined}
						disabled={disabled}
						mentionReadonly
					/>
				) : (
					<div className={styles.previewContainer}>
						<OpengraphMedia openGraph={opengraph} width={80} height={40} />
						<Span3 trim={1}>{isURL ? opengraph.title : opengraph.comment || placeholder}</Span3>
						{scheduledFor && (
							<CalendarButton
								value={scheduledFor}
								onChange={setScheduledFor}
								color="black"
								invert
								border={false}
								icon="schedule"
								loading={scheduling}
								disabled={disabled}
							/>
						)}
					</div>
				)}
				<Arrow
					className={classnames(open ? styles.tileElementOpen : null)}
					direction={open ? "up" : "down"}
					onClick={() => setOpen(c => !c)}
				/>
			</div>
			{open && (
				<div className="space">
					{(isLoading || isURL || opengraph.image || opengraph.video) && (
						<div className={classnames(styles.ogEdit, "space", isLoading ? styles.loadingOgMedia : "")}>
							{isLoading && <Loading position="center" />}
							{displayRemoveIcon && <Icon icon="close" className={styles.closeIcon} onClick={onRemove} />}
							<OpengraphMedia
								openGraph={opengraph}
								showControls
								className={isURL && styles.ogMediaCut}
								width={450}
								height={"100%"}
							/>

							{isURL && (
								<div className={styles.ogMetadataContainer}>
									{url && <div className={styles.ogMetadataUrl}>{url}</div>}
									<Text
										size={4}
										disabled={disabled}
										bold
										bare
										placeholder="Add a title."
										value={opengraph.title}
										onChange={title => updateOpengraph({title})}
									/>
									{["general", "facebook"].includes(network ?? "general") && (
										<div className={styles.description}>
											<Text
												bare
												disabled={disabled}
												value={opengraph.description}
												type="textarea"
												minRows={3}
												placeholder="Add a description."
												onChange={description => updateOpengraph({description})}
												ref={textareaRef}
											/>
										</div>
									)}
								</div>
							)}
						</div>
					)}
					<InputRow className={styles.footerToolbar}>
						<FileUpload
							iconSize={20}
							onChange={setFile}
							type={["image", "video"]}
							value={file}
							onlyIcon
							disabled={disabled}
						/>
						<Button
							disabled={disabled}
							icon="photo-library"
							border={false}
							invert
							onClick={mediaLibraryModal.open}
							color="black"
						/>
						<Separator />
						<EmojiPickerInput value="" onChange={handleEmojiChange} disabled={disabled} />
						{me.org.options.ai && (
							<Button
								loading={dirtyAIChat}
								invert
								border={false}
								disabled={(opengraph.comment ?? "").length <= 3 || disabled}
								icon="robot"
								onClick={() => updateAIChat({prompt: opengraph.comment})}
								color="black"
							/>
						)}
						<Separator />
						{scheduledFor ? (
							<CalendarButton
								disabled={disabled}
								size="small"
								value={scheduledFor}
								onChange={setScheduledFor}
								color="black"
								icon="schedule"
								loading={scheduling}
							/>
						) : (
							schedule &&
							peakTime && (
								<Button
									disabled={disabled}
									invert
									border={false}
									icon="schedule"
									onClick={schedule}
									color="black"
									value="Schedule using Peaktime™"
								/>
							)
						)}
					</InputRow>
				</div>
			)}
		</div>
	);
};
